import { useCallback, useEffect, useState } from 'react';
import { store } from 'redux/store';

import { Document } from 'types';
import documentTypes from 'constants/documentTypes';

import { getPartnerDocuments } from '../api/getPartnerDocuments';
import { getPartnerShareOwnershipCertificate } from '../api/getPartnerShareOwnershipCertificate';

type UseGetPartnerDocumentsProps = {
  partnerId: string;
};

const VALUESHARES = 'VALUESHARES';

export const useGetPartnerDocuments = ({
  partnerId,
}: UseGetPartnerDocumentsProps) => {
  const [documents, setDocuments] = useState<Document[]>([]);

  const [certificateValueSharesDocuments, setCertificateValueSharesDocuments] =
    useState<Document[]>([]);

  const [otherDocuments, setOtherDocuments] = useState<Document[]>([]);

  const [sharesOwnershipDocuments, setSharesOwnershipDocuments] = useState<
    Document[]
  >([]);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);

  const fetchDocuments = useCallback(async () => {
    try {
      setIsLoading(true);

      if (partnerId) {
        const [documents, certificatePayload] = await Promise.all([
          store.dispatch(getPartnerDocuments({ partnerId })),
          store.dispatch(getPartnerShareOwnershipCertificate({ partnerId })),
        ]);

        const certificateValueSharesDocs = documents.filter(
          (document: Document) =>
            document.category === documentTypes.CERTIFICATES &&
            document.subcategory === VALUESHARES
        );

        const otherDocs = documents.filter(
          (document: Document) =>
            document.category !== documentTypes.CERTIFICATES ||
            document.subcategory !== VALUESHARES
        );

        let ownershipCerts: Document[] = [];
        if (Array.isArray(certificatePayload)) {
          ownershipCerts = certificatePayload;
        } else if (certificatePayload) {
          ownershipCerts = [certificatePayload as Document];
        }

        setCertificateValueSharesDocuments(certificateValueSharesDocs);
        setOtherDocuments(otherDocs);
        setDocuments(documents);
        setSharesOwnershipDocuments(ownershipCerts.filter(Boolean));
      } else {
        setDocuments([]);
      }

      setIsLoading(false);
    } catch (error) {
      setIsError(true);
      setSharesOwnershipDocuments([]);
      setIsLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  return {
    data: {
      documents,
      certificateValueSharesDocuments,
      otherDocuments,
      sharesOwnershipDocuments,
    },
    isLoading,
    isError,
    actions: {
      fetchDocuments,
    },
  };
};
